import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useDestrierTheme, getCardSx } from '../ThemeContext';
import { useAuth } from '../AuthContext';
import { useDataCache } from '../DataCacheContext';

export default function SettingsPage() {
  const { c } = useDestrierTheme();
  const { user } = useAuth();
  const { clearAll: clearCache } = useDataCache();
  const outletContext = useOutletContext() || {};
  const profile = outletContext.profile;

  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [chessUsername, setChessUsername] = useState(profile?.chess_com_username || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || '');
      setChessUsername(profile.chess_com_username || '');
    }
  }, [profile]);

  const handleSave = async () => {
    const { trackSettingsChanged } = await import('../analytics');
    setSaving(true);
    setMessage('');

    const trimmedUsername = chessUsername.trim().toLowerCase();
    if (trimmedUsername && trimmedUsername !== profile?.chess_com_username) {
      try {
        const res = await fetch(`https://api.chess.com/pub/player/${trimmedUsername}`);
        if (!res.ok) {
          setMessage('Chess.com username not found.');
          setSaving(false);
          return;
        }
      } catch {
        setMessage('Could not verify chess.com username.');
        setSaving(false);
        return;
      }
    }

    const { error } = await supabase
      .from('profiles')
      .update({
        display_name: displayName.trim(),
        chess_com_username: trimmedUsername,
      })
      .eq('id', user.id);

    if (error) {
      setMessage('Failed to save. Please try again.');
    } else {
      setMessage('Settings saved!');
      trackSettingsChanged('profile');
      if (trimmedUsername !== profile?.chess_com_username) {
        clearCache();
        setTimeout(() => window.location.href = `/dashboard/weaknesses?username=${encodeURIComponent(trimmedUsername)}`, 1000);
      }
    }
    setSaving(false);
  };

  const cardSx = { ...getCardSx(c), p: 3, mb: 2.5 };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: c.textDark,
      backgroundColor: c.bg,
      borderRadius: '10px',
      '& fieldset': { borderColor: `${c.textMuted}30` },
      '&:hover fieldset': { borderColor: c.accent },
      '&.Mui-focused fieldset': { borderColor: c.accent },
    },
    '& .MuiInputLabel-root': { color: c.textMuted },
    '& .MuiInputLabel-root.Mui-focused': { color: c.accent },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 100px ${c.bg} inset`,
      WebkitTextFillColor: c.textDark,
    },
  };

  const labelSx = { fontSize: '0.78rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.5 };

  return (
    <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>
      <Box sx={{ pt: 10, pb: 8, px: { xs: 2, md: 4 }, maxWidth: 640, mx: 'auto' }}>
        <Typography sx={{ color: c.textDark, fontSize: { xs: '1.8rem', md: '2.2rem' }, fontWeight: 900, letterSpacing: '-0.03em', mb: 0.5 }}>
          Settings
        </Typography>
        <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', mb: 4 }}>
          Manage your account and preferences.
        </Typography>

        {/* Profile section */}
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: c.textDark, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            Profile
          </Typography>

          <Typography sx={labelSx}>Display Name</Typography>
          <TextField
            fullWidth
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your name"
            sx={{ ...textFieldSx, mb: 2.5 }}
            size="small"
          />

          <Typography sx={labelSx}>Chess.com Username</Typography>
          <TextField
            fullWidth
            value={chessUsername}
            onChange={(e) => setChessUsername(e.target.value)}
            placeholder="e.g. hikaru"
            sx={{ ...textFieldSx, mb: 2.5 }}
            size="small"
          />

          <Typography sx={labelSx}>Email</Typography>
          <TextField
            fullWidth
            value={user?.email || ''}
            disabled
            sx={{ ...textFieldSx, mb: 0, '& .Mui-disabled': { WebkitTextFillColor: c.textMuted } }}
            size="small"
          />
        </Box>

        {/* Save button + message */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
          <Button
            onClick={handleSave}
            disabled={saving}
            sx={{
              px: 4, py: 1.2,
              borderRadius: '12px',
              fontSize: '0.95rem',
              fontWeight: 700,
              textTransform: 'none',
              backgroundColor: c.accent,
              color: '#fff',
              '&:hover': { backgroundColor: '#7a68c9' },
              '&.Mui-disabled': { backgroundColor: `${c.accent}60`, color: '#fff9' },
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          {message && (
            <Typography sx={{ fontSize: '0.85rem', color: message.includes('saved') ? '#00A5C4' : '#E4446E', fontWeight: 600 }}>
              {message}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
